import { ArrowBackIos } from '@mui/icons-material';
import { Box, Button, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { Layout } from 'components';
import ToggleButton from 'components/ToggleButton';
import _ from 'lodash';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { resetCatalogToOrderStateAction } from 'store/actions/catalogToOrderActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import OrderWorkunitsDrawer from './Drawers';
import CatalogWorkunitDataGrid from './OrderWorkunitsDataGrids/CatalogWorkunitDataGrid/CatalogWorkunitDataGrid';
import SelectedWorkunitsDataGrid from './OrderWorkunitsDataGrids/SelectedWorkunitDatagrid/SelectedWorkunitsDataGrid';
import SummaryDataGrid from './OrderWorkunitsDataGrids/SummaryDataGrid/SummaryDataGrid';
import useOrderWorkunitApi from './services/useOrderWorkunitApi';
import OrderApiService from 'api/OrderApiService';
import useOrderWorkunitsService from './services/useOrderWorkunitsService';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { formatDateToddMMYYY } from 'helpers/format';
import TopBannerOrderWorkunits from './TopBannerOrderWorkunits/TopBannerOrderWorkunits';
import ManagementDialogs from 'features/Management/ManagementDialogs';

interface RouteParams {
  catalog_id: string;
  order_id: string;
}

const OrderWorkunits: React.FC<RouteComponentProps<RouteParams>> = ({ match }) => {
  const { dispatch } = useAppState();
  const state = useSelector((state) => state.catalogToOrder);
  const currentOrder = useSelector((state) => state.catalogToOrder.order);
  const selectedScope: number = useSelector(
    (state) => (state.catalogToOrder.orderHandler.searchParams?.scope as number) ?? 0
  );
  const { fetchWorkunitsInCatalog, getOrderWorkunits, getScopesByOrder, fetchOrder } = useOrderWorkunitApi();
  const { createSelectedWorkunits, updateSelectedWorkunitsMonthChanged, updateSearchFilters, getUniqScopesInOrder } =
    useOrderWorkunitsService();
  const [t] = useTranslation();
  const history = useHistory();
  const scopesInOrder = useMemo(() => getUniqScopesInOrder(), [getUniqScopesInOrder]);
  const [isCatalogWorkunitsOpen, setIsCatalogWorkunitsOpen] = useReducer((prev) => !prev, true);
  const [isOrderWorkunitOpen, setIsOrderWorkunitOpen] = useReducer((prev) => !prev, true);

  const {
    params: { catalog_id, order_id },
  } = match;

  useEffect(() => {
    const unloadCallback = (event: Event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      return '';
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => {
      dispatch(resetCatalogToOrderStateAction());
      window.removeEventListener('beforeunload', unloadCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentOrder) return;
    fetchWorkunitsInCatalog(Number(catalog_id));
    getPurchaseOrder(currentOrder);
    getUniqScopesInOrder();
    getScopesByOrder(Number(order_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);

  useEffect(() => {
    if (state.orderWorkunits.length > 0 && state.months.length > 0 && !state.isWorkunitsLoaded) {
      createSelectedWorkunits();
    }
  }, [state.orderWorkunits, state.months, createSelectedWorkunits, state.isWorkunitsLoaded]);

  useEffect(() => {
    if (state.selectedWorkunits.length === 0 && state.months.length === 0) return;
    updateSelectedWorkunitsMonthChanged(state.months);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.months.length, state.selectedWorkunits.length]);

  useEffect(() => {
    if (!!Number(order_id) && !!Number(catalog_id))
      Promise.all([fetchOrder(Number(order_id)), getScopesByOrder(Number(order_id))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id, catalog_id]);

  useEffect(() => {
    if (state.months.length === 0) return;
    getOrderWorkunits(Number(order_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id, state.months]);

  const handleScopeFilterChange = (event: SelectChangeEvent<number>) => {
    updateSearchFilters({ scope: event.target.value ?? 0 }, 'selected');
  };

  const getPurchaseOrder = async (currentOrder: any) => {
    const purchaseOrderList = await OrderApiService.fetchEditPurchaseOrder(Number(order_id), currentOrder?.affair?.otp);
    const poList = purchaseOrderList
      ?.filter((item: { is_archived: boolean }) => item.is_archived === false)
      ?.map(
        (item: {
          is_valid: boolean;
          id: number;
          poref: string;
          is_archived: any;
          poend: string;
          reference: string;
          postart: string;
        }) => ({
          id: item.id,
          name: item.poref,
          is_valid: item.is_valid,
          reference: `${item.poref} (${formatDateToddMMYYY(item.postart)} - ${formatDateToddMMYYY(item.poend)})`,
        })
      );
    dispatch(getPurchaseOrderAction(poList));
  };

  const orderWorkunitsContent = (
    <>
      <Button
        onClick={() => history.goBack()}
        startIcon={<ArrowBackIos />}
        sx={{ mb: 2, alignSelf: 'flex-start', pl: 0 }}
      >
        {t('Back')}
      </Button>
      {state?.order && <TopBannerOrderWorkunits />}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" color="neutral.main" sx={{ mr: 1 }}>
          {t('Catalog')}
        </Typography>
        <ToggleButton
          isOpen={isCatalogWorkunitsOpen}
          handleClick={() => setIsCatalogWorkunitsOpen()}
          disabled={false}
        />
      </Box>
      {isCatalogWorkunitsOpen && <CatalogWorkunitDataGrid />}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" color="neutral.main" sx={{ mr: 1 }}>
          {t('My_order')}
        </Typography>
        <ToggleButton isOpen={isOrderWorkunitOpen} handleClick={() => setIsOrderWorkunitOpen()} disabled={false} />
      </Box>
      {isOrderWorkunitOpen && (
        <>
          <Box sx={{ maxWidth: '200px', mb: 2 }}>
            <FormControl fullWidth>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                variant="filled"
                onChange={handleScopeFilterChange}
                value={selectedScope}
                size="small"
              >
                <MenuItem value={0}>{t('All_project')}</MenuItem>
                {scopesInOrder.length &&
                  scopesInOrder?.map((scopeInOrder) => (
                    <MenuItem key={scopeInOrder?.id} value={scopeInOrder?.id}>
                      {scopeInOrder?.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <SelectedWorkunitsDataGrid />
        </>
      )}
    </>
  );

  const orderWorkunitsSummary = <SummaryDataGrid />;

  return (
    <Layout path="order" name="Workunits">
      {orderWorkunitsContent}
      <OrderWorkunitsDrawer />
      <ManagementDialogs />
      <Box sx={{ m: 'auto -30px -30px', p: '30px', backgroundColor: '#FFFFFF', boxShadow: '0px 3px 16px #B2BCD571' }}>
        {orderWorkunitsSummary}
      </Box>
    </Layout>
  );
};
export default OrderWorkunits;
